import _ from 'lodash';
import { createContext, ReactNode, RefObject, useRef, useContext, useMemo } from 'react';
import {
  SearchStore,
  SearchStoreState,
  createSearchStore,
  CreateSearchStoreParams,
} from './SearchStore';
import { MagicSearchPlugin } from './plugins';
import { useStore } from 'zustand';
import { shallow } from 'zustand/shallow';
import { StartEvents } from './StartEvents';
import { StartPlugins } from './StartPlugins';

const DEFAULT_CONTEXT_VALUE = {
  listRef: null,
  searchStore: null,
  plugins: [],
};

const MagicSearchContext = createContext<{
  listRef: RefObject<HTMLDivElement> | null;
  searchStore: SearchStore | null;
  plugins: MagicSearchPlugin[];
}>(DEFAULT_CONTEXT_VALUE);

type MagicSearchProviderProps = CreateSearchStoreParams & {
  plugins: MagicSearchPlugin[];
  children: ReactNode | ReactNode[];
};

export function MagicSearchProvider({
  children,
  plugins,
  ...createSearchStoreProps
}: MagicSearchProviderProps) {
  const listRef = useRef<HTMLDivElement>(null);
  const searchStoreRef = useRef<SearchStore>();
  if (!searchStoreRef.current) {
    searchStoreRef.current = createSearchStore(createSearchStoreProps);
  }

  const value = useMemo(
    () => ({
      listRef,
      searchStore: searchStoreRef.current! as SearchStore,
      plugins,
    }),
    [plugins],
  );

  return (
    <MagicSearchContext.Provider value={value}>
      <StartEvents />
      <StartPlugins />
      {children}
    </MagicSearchContext.Provider>
  );
}

export function useMagicSearchContext() {
  const magicSearchContext = useContext(MagicSearchContext);
  if (!magicSearchContext) throw new Error('Missing magicSearchContext in the tree');
  return magicSearchContext;
}

function useMagicSearchStore() {
  const { searchStore } = useMagicSearchContext();
  if (!searchStore) throw new Error('Missing searchStore in the tree');
  return searchStore;
}

function useMagicSearchPlugins() {
  const { plugins } = useMagicSearchContext();
  if (!plugins) throw new Error('Missing plugins in the tree');
  return plugins;
}

export function useMagicSearch<T>(selector: (state: SearchStoreState) => T) {
  const searchStore = useMagicSearchStore();
  return useStore(searchStore, selector, shallow);
}

export function useMagicSearchLinkProps() {
  const plugins = useMagicSearchPlugins();
  const searchStore = useMagicSearchStore();

  return {
    onClick: () => {
      plugins.forEach(plugin => {
        if ('onLinkItemClick' in plugin && _.isFunction(plugin.onLinkItemClick)) {
          plugin.onLinkItemClick(searchStore?.getState().state);
        }
      });
      searchStore?.getState().onResetStore();
    },
  };
}
